/**
 * The two accessibility preferences, shared by both sides (§R).
 *
 * They live here rather than in `src/frontend/a11y.ts` for the reason `shared/` exists at all: the
 * BROWSER decides what to offer and applies it, the SERVER validates and stores it, and the two must
 * agree on the vocabulary or the app offers a scale the route rejects. That is exactly the
 * hand-mirrored-payload trap `src/frontend/CLAUDE.md` names, and it is worth the file.
 *
 * ⚠ **Nothing in here is user-facing text.** These are WIRE values - they land in a `settings` row
 * and in a zod enum - so they never go through `t()`; the labels beside them in the Settings card are
 * ordinary chrome copy and are keys (`shared/constants.ts`'s header comment has the three-way split).
 */

/** The multiplier applied to the root font size. 1 = the browser's own default text size. */
export type TextScale = number;

/**
 * ⚠ Not a free number, and the reason is the constraint the whole feature is built around: **text
 * scales, layout holds.** Every box, gap and control in this app is `px` on purpose (see the `html`
 * rule in `public/style.css`), so past a point the text stops fitting the boxes it is in and the
 * setting starts breaking the app instead of opening it. 1.4 is where a 13px hint reaches ~18px and
 * the card chrome still contains it; someone who needs more than that needs browser zoom, which
 * scales the boxes too and which every browser already has.
 *
 * 0.9 is here because the owner asked for a *multiplier*, not a magnifier - wanting more rows on a
 * small laptop screen is the same preference pointing the other way.
 */
export const TEXT_SCALES: readonly TextScale[] = [0.9, 1, 1.15, 1.25, 1.4];
export const DEFAULT_TEXT_SCALE: TextScale = 1;

/**
 * `"system"` follows `prefers-reduced-motion` - the platform answer, which round 149 already honors
 * and which is right for nearly everyone. The other two exist because **a preference that can only
 * AGREE with the OS is a readout rather than a setting**, which is the half §R was filed for:
 *
 * - `"reduced"` for someone who cannot change the OS setting (a managed or shared laptop), or who
 *   wants less motion in THIS app and not in every other one - there is no OS spelling for that.
 * - `"full"` for someone whose OS preference is set for a reason that has nothing to do with this
 *   app's animation (a remote desktop, a driver bug, a borrowed machine) and who wants it back.
 */
export const MOTION_PREFS = ["system", "reduced", "full"] as const;
export type MotionPref = (typeof MOTION_PREFS)[number];
export const DEFAULT_MOTION: MotionPref = "system";

/**
 * Contrast, and it is the same three-way shape as motion for the same reason (owner ask, 2026-08-05:
 * *"remember the WCAG stuff for colors for the a11y page too"*).
 *
 * `"system"` follows `prefers-contrast: more`. `"high"` selects the app's third and fourth palettes
 * (`public/style.css`'s `[data-contrast="high"]` blocks), which are real token sets measured by
 * `tests/theme.test.ts` like the other two - not a filter over the pixels, which would also "fix" the
 * cover art and every chart and could not be checked at all. `"standard"` is an explicit refusal, and
 * it has to exist separately from `"system"`: someone whose OS is in high contrast for a reason that
 * has nothing to do with this app needs a way to say so, and a setting that can only agree with the
 * platform is a readout.
 */
export const CONTRAST_PREFS = ["system", "standard", "high"] as const;
export type ContrastPref = (typeof CONTRAST_PREFS)[number];
export const DEFAULT_CONTRAST: ContrastPref = "system";

export interface A11yPrefs {
	textScale: TextScale;
	motion: MotionPref;
	contrast: ContrastPref;
}

export function isTextScale(value: unknown): value is TextScale {
	return typeof value === "number" && TEXT_SCALES.includes(value);
}

export function isMotionPref(value: unknown): value is MotionPref {
	return typeof value === "string" && (MOTION_PREFS as readonly string[]).includes(value);
}

export function isContrastPref(value: unknown): value is ContrastPref {
	return typeof value === "string" && (CONTRAST_PREFS as readonly string[]).includes(value);
}
